import { FILE_ALLOWED_CONTENT_TYPES, FILE_MAX_SIZE_BYTES, resolveContentType } from '@atlas/types'
import { formatBytes } from '@atlas/ui-domain'

/**
 * The smallest file this client will attempt to upload. The backend's File
 * creation contract requires a strictly positive declared size, and a browser-
 * selected empty file has no bytes to checksum or transfer — so rather than let
 * one reach `POST /files` and fail there with a generic validation error, it is
 * rejected locally, before any request, with a message that says what happened.
 */
const MIN_UPLOAD_FILE_SIZE_BYTES = 1

/**
 * Whether a file of this size has nothing to upload. Exported so a surface that
 * words the rejection itself — one that speaks in its own voice rather than
 * this module's — still reads the threshold from here instead of restating it.
 */
export function isEmptyUploadSize(size: number): boolean {
	return size < MIN_UPLOAD_FILE_SIZE_BYTES
}

/**
 * Why a file of this size cannot be uploaded, or `null` if it can — the size
 * half of {@link rejectionReason}, on its own.
 *
 * Split out because the transport seam needs the size bounds without the
 * content-type check: it serves a caller that deliberately overrides the
 * browser's MIME type and would fail a type check that is correct for everyone
 * else. Both callers produce the same wording, because the user is told the
 * same thing either way.
 */
export function sizeRejectionReason(size: number): string | null {
	if (isEmptyUploadSize(size)) {
		return 'Empty files cannot be uploaded.'
	}
	if (size > FILE_MAX_SIZE_BYTES) {
		return `File exceeds the ${formatBytes(FILE_MAX_SIZE_BYTES)} size limit`
	}
	return null
}

/**
 * Why this file cannot be uploaded, or `null` if it can.
 *
 * Runs before anything is sent, so a rejection here produces no request and no
 * server-side trace — which is why the reason has to be specific enough to act
 * on. The server re-checks everything anyway (and fingerprints the actual bytes
 * on confirm); this only spares the user a round trip.
 *
 * Emptiness is decided before the content type, deliberately: a zero-byte file
 * with an unrecognised type is empty first, and saying so is what the user can
 * act on.
 */
export function rejectionReason(file: File): string | null {
	if (isEmptyUploadSize(file.size)) {
		return sizeRejectionReason(file.size)
	}
	// Resolved, not `file.type` raw: browsers disagree about formats they do not
	// know. Safari reports a non-canonical type for a `.psd` where Chrome reports
	// the registered one, so trusting the raw value rejected the same file in one
	// browser and accepted it in the other — silently, since this check runs
	// before any request.
	const contentType = resolveContentType(file.name, file.type)
	if (!(FILE_ALLOWED_CONTENT_TYPES as readonly string[]).includes(contentType)) {
		return `File type '${contentType || 'unknown'}' is not allowed`
	}
	return sizeRejectionReason(file.size)
}
